import { BUNDLE_TAG, BUNDLE_UNDO_REDO_HISTORY_TAG } from '../constants';
import { api } from '../core';
import { type RootState, makeSelectBundlePageDataByPageId, setBundlePageRotation } from '@/store';

/**
 * Rotation direction sent to the backend. The server applies it on top of
 * the page's existing rotation, rotates the underlying PDF blob, and persists
 * the new state. The canonical rotation is then read from /bundle.
 */
export type RotatePageDirection = 'clockwise' | 'anticlockwise';

interface RotatePageProps {
  bundleId: string;
  pageId: string;
  direction: RotatePageDirection;
}

/**
 * Normalize an arbitrary rotation value to one of 0 | 90 | 180 | 270.
 * Handles negative results from anticlockwise rotation and values that may
 * have drifted outside the canonical range.
 */
const normalizeRotation = (value: number): number => ((value % 360) + 360) % 360;

const DIRECTION_TO_DELTA: Record<RotatePageDirection, number> = {
  clockwise: 90,
  anticlockwise: -90,
};

const rotatePageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    rotatePage: builder.mutation<unknown, RotatePageProps>({
      query: ({ bundleId, pageId, direction }: RotatePageProps) => ({
        url: 'pages/rotate',
        method: 'POST',
        body: {
          bundle_id: bundleId,
          page_id: pageId,
          direction,
        },
      }),
      async onQueryStarted({ bundleId, pageId, direction }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState;
        const activeBundleType = state.sharedData.activeBundleType;
        if (!activeBundleType) {
          return;
        }

        const getBundlePageDataByPageId = makeSelectBundlePageDataByPageId(state);
        const currentPage = getBundlePageDataByPageId(pageId);
        const previousRotation = currentPage?.pageDimensions.pageRotation ?? 0;
        const optimisticRotation = normalizeRotation(previousRotation + DIRECTION_TO_DELTA[direction]);

        /**
         * Optimistic update — patch the single page's rotation in the Redux
         * slice so the client-side `rotate` prop changes immediately and the
         * user sees the page rotated the moment they click. This is purely
         * for perceived snappiness; the canonical state is whatever the
         * backend serves on the next /bundle fetch (triggered below).
         */
        dispatch(
          setBundlePageRotation({
            key: activeBundleType,
            pageId,
            pageRotation: optimisticRotation,
          }),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Rollback the optimistic value before the refetch lands.
          dispatch(
            setBundlePageRotation({
              key: activeBundleType,
              pageId,
              pageRotation: previousRotation,
            }),
          );
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in rotatePage in onQueryStarted:', error);
          }
        } finally {
          /**
           * Post-success (and post-rollback) reconcile — the backend rotates
           * the underlying PDF blob, not just metadata, so the displayed
           * image only updates when /bundle is refetched and react-pdf
           * reloads the new pageUri (the SAS signature in the URL changes
           * on every fetch, which busts the Document key in
           * BasePdfSinglePageRenderer and triggers a reload). Running this
           * in `finally` covers both the success path and the rollback
           * path with one invalidate.
           */
          dispatch(
            api.util.invalidateTags([
              { type: BUNDLE_TAG, id: bundleId },
              { type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: bundleId },
            ]),
          );
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useRotatePageMutation } = rotatePageApi;
